import React, { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { TokenContext } from "./tokenContext";
import "./PaymentSuccessful.css";
import { getAuth } from "firebase/auth";
import { doc, getDoc, updateDoc, addDoc, collection, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../utils/firebase";
import { v4 as uuidv4 } from "uuid";
import Spinner from "./Spinner";

export default function PaymentSuccessful() {
  const navigate = useNavigate();
  const { balance, setBalance } = useContext(TokenContext);

  // UI state
  const [status, setStatus] = useState("Pending");
  const [receiptUrl, setReceiptUrl] = useState("");
  const [prevTokens, setPrevTokens] = useState(0);
  const [desiredTokens, setDesiredTokens] = useState(0);
  const [transactionId, setTransactionId] = useState("");
  const [hasCheckedPayment, setHasCheckedPayment] = useState(false);

  // one‐off log id
  const uniqueLogId = React.useMemo(() => uuidv4(), []);

  // pick up the Stripe session
  const sessionId = new URLSearchParams(window.location.search).get("session_id");

  // ---- Header status paint (POST only) — guarded to never downgrade success
  
  useEffect(() => {
    if (!sessionId) return;

    let alive = true;
    const ctrl = new AbortController();
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

    (async function poll() {
      // optional: reflect we're waiting
      setStatus?.("Pending");

      for (let i = 0; i < 60 && alive; i++) { // ~2 minutes @ 2s
        try {
          const r = await fetch("https://fluency.ispeakwell.ca/api/payment-status", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ sessionId }),
            signal: ctrl.signal,
          });

          if (!r.ok) {
            // server warming or transient error; don't treat as failure
            await sleep(2000);
            continue;
          }

          const j = await r.json();
          if (!alive) return;

          // If we have a receipt URL, it's definitely paid.
          if (j.receipt_url) {
            setReceiptUrl?.(j.receipt_url);
            setStatus?.("Successful");
            try { ctrl.abort(); } catch {}
            return;
          }

          switch (j.status) {
            case "succeeded":
            case "paid":
            case "requires_capture":
              setStatus?.("Successful");
              try { ctrl.abort(); } catch {}
              return;

            case "processing":
            case "requires_action":
            case "requires_confirmation":
              setStatus?.("Pending");
              await sleep(2000);
              break;

            case "requires_payment_method":
            case "canceled":
              setStatus?.("Failed");
              try { ctrl.abort(); } catch {}
              navigate?.("/paymentnotsuccessful");
              return;

            default:
              await sleep(2000);
          }
        } catch (err) {
          if (err?.name === "AbortError") return; // normal on cleanup or success
          console.error("Payment status fetch failed:", err);
          await sleep(2000); // back off and retry
        }
      }

      // Timeout: don't mark failed—just say we're still verifying
      if (alive) {
        setStatus?.("Verifying");
        try { ctrl.abort(); } catch {}
      }
    })();

    return () => {
      alive = false;
      try { ctrl.abort(); } catch {}
    };
  }, [sessionId, navigate]);


  // --------------------------------------------------------------------------
  // SINGLE EFFECT FOR EVERYTHING: POLLING + FIRESTORE UPDATES
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (!sessionId || hasCheckedPayment) return;

    let attempts = 0;
    const MAX_ATTEMPTS  = 10;
    const INITIAL_DELAY = 3000;
    const MAX_DELAY     = 60000;

    const timerRef = { id: null };
    let stopped = false;

    const controller = new AbortController();

    async function logTokenTransfer(userId, qty, label, receipt) {
      const ref = collection(db, "tokenTransfers");
      await addDoc(ref, {
        date: new Date(),
        userId,
        sessionId,
        uniqueLogId,
        tokens: qty,
        status: label,
        receiptUrl: receipt,
      });
    }

    async function transferTokens(receipt) {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error("No authenticated user");

      const userRef = doc(db, "users", user.uid);
      const snap = await getDoc(userRef);
      if (!snap.exists()) throw new Error("User doc missing");

      const data = snap.data();
      const want = data.desiredPurchase || 0;
      const have = data.tokens || 0;

      // bonus
      let finalTokensReceived = want;
      let bonusMultiplier = 1;
      if (want >= 50) { finalTokensReceived = want * 3; bonusMultiplier = 3; }
      else if (want >= 30) { finalTokensReceived = want * 2; bonusMultiplier = 2; }

      // write balance
      await updateDoc(userRef, {
        tokens: have + finalTokensReceived,
        desiredPurchase: 0,
        paying: false,
        receiptUrl: receipt,
      });

      // receipt subdoc
      const receiptRef = doc(db, "users", user.uid, "purchaseReceipts", uniqueLogId);
      await setDoc(receiptRef, {
        createdAt: serverTimestamp(),
        appReceiptNumber: uniqueLogId,
        sessionId,
        tokensPurchased: want,
        bonusMultiplier,
        tokensCredited: finalTokensReceived,
        prevTokens: have,
        newBalance: have + finalTokensReceived,
        stripeReceiptUrl: receipt || "",
        status: "Successful",
      });

      // UI
      setPrevTokens(have);
      setDesiredTokens(want);
      if (typeof setBalance === "function") {
        setBalance(have + finalTokensReceived);
      } else {
        console.warn("TokenContext.setBalance missing; is TokenProvider wrapping this route?");
      }
      setTransactionId(uniqueLogId);

      // log
      await logTokenTransfer(user.uid, want, "Successful", receipt);
    }

    function scheduleNext(fn, ms) {
      if (stopped) return;
      timerRef.id = setTimeout(fn, ms);
    }

    async function poll() {
      if (stopped) return;
      attempts += 1;

      try {
        const res = await fetch("https://fluency.ispeakwell.ca/api/payment-status", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sessionId }),
          signal: controller.signal,
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        let data = {};
        const text = await res.text();
        if (text) { try { data = JSON.parse(text); } catch {} }

        const looksSuccessful =
          !!data.receipt_url || data.status === "succeeded" || data.status === "paid";

        if (looksSuccessful) {
          try {
            const receipt = data.receipt_url || "";
            if (receipt) setReceiptUrl(receipt);
            await transferTokens(receipt);
            setStatus((prev) => (prev === "Successful" ? prev : "Successful"));
          } catch (e) {
            console.error("Transfer error:", e);
            setStatus("Failed");
            navigate("/paymentnotsuccessful");
          } finally {
            stopped = true;
            setHasCheckedPayment(true);
          }
        } else if (attempts < MAX_ATTEMPTS) {
          const delay = Math.min(INITIAL_DELAY * 2 ** (attempts - 1), MAX_DELAY);
          scheduleNext(poll, delay);
        } else {
          setStatus("Failed");
          stopped = true;
          setHasCheckedPayment(true);
          navigate("/paymentnotsuccessful");
        }
      } catch (err) {
        // Abort is expected when success or unmount cleans up the controller.
        if (err?.name === "AbortError" || controller.signal.aborted || stopped) return;
        console.error("Polling error:", err);
        if (attempts < MAX_ATTEMPTS && !stopped) {
          const delay = Math.min(INITIAL_DELAY * 2 ** (attempts - 1), MAX_DELAY);
          scheduleNext(poll, delay);
        } else {
          setStatus("Failed");
          stopped = true;
          setHasCheckedPayment(true);
          navigate("/paymentnotsuccessful");
        }
      }
    }

    // Removed the old GET header call to avoid 404s & races.

    poll();

    return () => {
      stopped = true;
      controller.abort();
      if (timerRef.id) clearTimeout(timerRef.id);
    };
  
  }, [sessionId, hasCheckedPayment, navigate, setBalance, uniqueLogId]);

  // UI helpers
  const openReceipt = () => window.open(receiptUrl, "_blank");
  const copyText   = (txt) => navigator.clipboard.writeText(txt).then(() => alert("Copied!"));

  return (
    <div className="PYS-Menu-container">
      <h1 className="PYS-header-title">The iSpeakWell Project</h1>
      <h5 className="PYS-tagLine">
        Become a member of the iSpeakWell crew.
      </h5>
      <hr/>

      <div>
        <h1 className="PYS-Task-Menu">Payment {status}</h1>
        {status === "Pending"    && <p>Your transaction is being processed…</p>}
        {status === "Successful" && <p>Your tokens have been added—thank you! 🎉</p>}
        {status === "Failed"     && <p>Something went wrong; please reach out or try again.</p>}
      </div>

      {!transactionId ? (
        <div style={{ textAlign: "center" }}>
          <Spinner/>
          <p>Processing your purchase…</p>
        </div>
      ) : (
        <div className="PYS-Transaction">
          <div className="PYS-Tasks">
            <h2 className="PYS-Task-Menu">Thank you for your purchase.</h2>
            <h4 className="PYS-Task-Menu">Prior Token Count: {prevTokens}</h4>
            <h4 className="PYS-Task-Menu">+ Purchased Tokens: {desiredTokens}</h4>
            <h4 className="PYS-Task-Menu">New Token Count: {balance}</h4>
            <hr/>
          </div>

          <div className="PYS-Transaction">
            <h5 className="PYS-TransactionNumber">Transaction #: {transactionId}</h5>
            <button onClick={() => copyText(transactionId)}>Copy Transaction #</button>

            <h5 className="PYS-TransactionNumber">Receipt URL:</h5>
            <p>{receiptUrl}</p>
            <button onClick={openReceipt}>View Receipt</button>
            <button onClick={() => copyText(receiptUrl)}>Copy URL</button>
          </div>

          <hr/>

          <div className="PYS-ContinuingOn">
            <h2 className="PYS-Task-Menu">Select a task to continue</h2>
            <button onClick={() => navigate("/voicebot")}>The iSpeakWell Project</button>
            <div className="PYS-BTN-HLDR">
              <button onClick={() => navigate("/resumerevisor")}>Resume & Cover Letter</button>
              <button onClick={() => navigate("/interview-practice")}>Interview Practice</button>
              <button onClick={() => navigate("/conversationpractice")}>Conversation Practice</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
